import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnDestroy, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { StateService } from '../../services/state';
import { Api } from '../../services/api';
import { HttpHeaders } from '@angular/common/http';
import { Notifications } from '../../services/notifications';
import detailsJson from '../../models/details.json';
import { UtilsService } from '../../services/utils';
import { SeguroDiasClock, SeguroDiasClockService } from '../../services/seguro-dias-clock';

// Última hora del dia (Madrid): a partir d'aquí es mostra l'avís reforçat i
// cal confirmació explícita per pagar. Aïllat aquí per si es vol fer
// configurable més endavant.
const LAST_MINUTE_WARNING_THRESHOLD_SECONDS = 60 * 60;

// Mentre el pagament està bloquejat, refresquem el clock periòdicament perquè
// el botó torni a funcionar sol quan passi la finestra, sense recarregar.
const BLOCK_POLL_INTERVAL_MS = 20 * 1000;

@Component({
  selector: 'app-contract-preview',
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatSelectModule,
    MatCardModule,
    MatIconModule,
    MatCheckboxModule,
  ],
  templateUrl: './contract-preview.html',
  styleUrl: './contract-preview.scss',
})
export class ContractPreview implements OnDestroy {
  private readonly router = inject(Router);
  private readonly stateService = inject(StateService);
  private readonly api = inject(Api);
  private readonly notification = inject(Notifications);
  private readonly utils = inject(UtilsService);
  private readonly clockService = inject(SeguroDiasClockService);
  private readonly cdr = inject(ChangeDetectorRef);

  stateData: any;
  public detailsData: any = detailsJson;
  public expandedDetail: string | null = null;

  clock: SeguroDiasClock | null = null;
  lastMinuteConfirmed = false;
  // Cert des del clic a "Ir al pago" fins que marxem a Redsys o el pagament
  // es talla: mentre dura, el botó queda desactivat.
  paying = false;
  private blockPollTimer: ReturnType<typeof setTimeout> | null = null;

  // El dia civil del pagament és avui (hora Madrid) i queda menys d'una hora
  // per mitjanit: cal l'avís reforçat i confirmació explícita abans de pagar.
  get showLastMinuteWarning(): boolean {
    if (this.showMaintenanceBlock) return false;
    if (!this.clock) return false;
    if (this.clock.secondsToMidnight > LAST_MINUTE_WARNING_THRESHOLD_SECONDS) return false;
    return this.civilDayOf(this.stateData?.start_date) === this.clock.today;
  }

  // Finestra de bloqueig (23:45-00:00 per defecte) activa i l'inici demanat
  // és avui: el backend rebutjaria el pagament, així que ni l'intentem.
  get showMaintenanceBlock(): boolean {
    if (!this.clock?.blockActive) return false;
    return this.civilDayOf(this.stateData?.start_date) === this.clock.today;
  }

  ngOnInit() {
    this.stateData = this.stateService.getState();

    if (
      !this.stateData ||
      !this.stateData.idVersionDivina ||
      !this.stateData.duration ||
      !this.stateData.tomador?.name ||
      !this.stateData.checkCondiciones
    ) {
      this.router.navigate(['/']);
      return;
    }

    this.refreshClock(() => {
      if (this.isStartDateInPast()) {
        this.warnStartDateExpiredAndGoToPriceResult();
      }
    });
  }

  // Dia civil tal com l'envia pay() al backend (substring de l'ISO pinnat a
  // mitjanit UTC mantenint el dia local): és el mateix càlcul que fa el
  // backend amb start_date, per poder comparar-lo amb clock.today.
  private civilDayOf(date: any): string | null {
    return this.utils.toIsoAtUtcMidnightKeepDay(date)?.substring(0, 10) ?? null;
  }

  private refreshClock(onDone?: () => void) {
    this.clockService.fetch().subscribe({
      next: (clock) => {
        this.clock = clock;
        this.cdr.markForCheck();
        this.scheduleBlockPollingIfNeeded();
        onDone?.();
      },
      // Si no podem consultar el rellotge no bloquegem res més: la garantia
      // real és la validació del backend a pay-form.
      error: () => onDone?.(),
    });
  }

  // Mentre el banner de manteniment és visible, torna a consultar el clock
  // periòdicament: quan la finestra passi el botó es desbloqueja sol.
  private scheduleBlockPollingIfNeeded() {
    if (this.blockPollTimer) {
      clearTimeout(this.blockPollTimer);
      this.blockPollTimer = null;
    }
    if (!this.showMaintenanceBlock) return;

    this.blockPollTimer = setTimeout(() => this.refreshClock(), BLOCK_POLL_INTERVAL_MS);
  }

  ngOnDestroy() {
    if (this.blockPollTimer) {
      clearTimeout(this.blockPollTimer);
    }
  }

  // Comparació amb el dia de Madrid que dona el servidor, mai amb el rellotge
  // del navegador. Sense clock no bloquegem: la garantia real és la validació
  // del backend a pay-form.
  private isStartDateInPast(): boolean {
    const startDay = this.civilDayOf(this.stateData?.start_date);
    if (!this.clock || !startDay) return false;
    return startDay < this.clock.today;
  }

  private warnStartDateExpiredAndGoToPriceResult() {
    this.notification.showInfo(
      'La fecha de inicio seleccionada ya ha pasado; hemos actualizado tu presupuesto a la fecha de hoy.'
    );
    this.router.navigate(['/price-result']);
  }

  // El backend és qui manté la finestra de bloqueig com a garantia; si el
  // clock que teníem al front encara no la reflectia, la forcem aquí perquè
  // aparegui el mateix banner sense esperar el pròxim refresh.
  private handlePaymentWindowClosedByServer() {
    if (this.clock) {
      this.clock = { ...this.clock, blockActive: true };
    }
    this.cdr.markForCheck();
    this.scheduleBlockPollingIfNeeded();
  }

  pay() {
    // Evita el doble clic: cada pay-form crea una cotització nova a Divina.
    if (this.paying) return;
    this.paying = true;

    // Refresquem el rellotge just abans de pagar: evita decidir amb dades
    // velles si l'usuari ha trigat a arribar fins aquí.
    this.refreshClock(() => {
      if (this.isStartDateInPast()) {
        this.stopPaying();
        this.warnStartDateExpiredAndGoToPriceResult();
        return;
      }

      if (this.showMaintenanceBlock) {
        // El banner ja apareix sol (getter reactiu); no intentem el pagament.
        this.stopPaying();
        return;
      }

      if (this.showLastMinuteWarning && !this.lastMinuteConfirmed) {
        this.stopPaying();
        this.notification.showInfo(
          'Marca la casilla de confirmación antes de continuar con el pago.'
        );
        return;
      }

      this.submitPayment();
    });
  }

  // Si l'usuari torna enrere des de Redsys, el navegador pot restaurar la
  // pàgina del bfcache amb paying encara a cert: tornem a activar el botó.
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent) {
    if (event.persisted) {
      this.stopPaying();
    }
  }

  private stopPaying() {
    this.paying = false;
    this.cdr.markForCheck();
  }

  private submitPayment() {
    const normDate = (d: any) => this.utils.toIsoAtUtcMidnightKeepDay(d);
    const normPerson = (p: any) => p ? {
      ...p,
      birthday: normDate(p.birthday),
      licenseDate: normDate(p.licenseDate),
    } : p;

    let correctedDateState = Object.assign({}, this.stateData, {
      start_date: normDate(this.stateData.start_date),
      end_date: normDate(this.stateData.end_date),
      tomador: normPerson(this.stateData.tomador),
      conductor: normPerson(this.stateData.conductor),
      propietari: normPerson(this.stateData.propietari),
    });

    this.api
      .post('/seguro-dias/pay-form', correctedDateState, {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
      })
      .subscribe(
        (data: any) => {
          // Només mantenim el botó desactivat si marxem cap a Redsys.
          if (!data?.datosTpv) {
            this.stopPaying();
          }

          if (data && data.error === 'START_DATE_IN_PAST') {
            this.warnStartDateExpiredAndGoToPriceResult();
            return;
          }

          if (data && data.error === 'PAYMENT_WINDOW_CLOSED') {
            this.handlePaymentWindowClosedByServer();
            return;
          }

          if (data && data.error === 'ASSISTANCE_MIN_DAYS') {
            this.notification.showError(data.message);
            return;
          }

          if (!data || data.errors || !data.datosTpv) {
            if (data?.errors?.AddCotizacion) {
              this.notification.showError(
                data.errors.AddCotizacion[0].additionalInfo ||
                  'Ha ocurrido un error al validar la cotización, comprueba los datos e inténtalo de nuevo.'
              );
              return;
            }

            this.notification.showError(
              'Ha ocurrido un error al enviar el formulario. Por favor, inténtalo de nuevo más tarde.'
            );
            return;
          }
          this.stateService.setState(Object.assign(this.stateData, { quotation: data }));
          this.redirectToRedsys(data.datosTpv);
        },
        (error) => {
          //TODO notify error
          this.stopPaying();
          this.notification.showError(
            'Ha ocurrido un error al enviar el formulario. Por favor, inténtalo de nuevo más tarde.'
          );
        }
      );
  }

  redirectToRedsys(data: any) {
    // Crear formulari
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = data.urlPost;
    form.style.display = 'none'; // ocult

    // Helper per afegir inputs ocults
    function addHiddenInput(name: string, value: string) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    // Camps que espera Redsys
    addHiddenInput('Ds_SignatureVersion', data.signatureVersion);
    addHiddenInput('Ds_MerchantParameters', data.merchantParameters);
    addHiddenInput('Ds_Signature', data.signature);

    // Afegir formulari al DOM i enviar-lo
    document.body.appendChild(form);
    form.submit();
  }

  goToForm() {
    this.router.navigate(['/price-result']);
  }

  goToHome() {
    this.router.navigate(['/price-form/' + this.stateData?.vehicleCategory]);
  }

  goToContractForm() {
    this.router.navigate(['/contract-form']);
  }
}

//{"id":30257795,"primaNeta":15.0,"primaTotal":16.39,"datosTpv":{
// "signature":"lzrFO56HE/1d6LDaWqpiu/WkdcFiLNuZLeH3afIVYok=",
// "signatureVersion":"HMAC_SHA256_V1",
// "merchantParameters":"eyJEU19NRVJDSEFOVF9NRVJDSEFOVFVSTCI6Imh0dHBzOi8vZ2F0ZXdheS10cHYtc3RnLXdhcHAuYXp1cmV3ZWJzaXRlcy5uZXQvbm90aWZpY2FjaW9uVHB2UmVkaXJlY2Npb25BdXRvc0RpYS9lbkJhalBhdyIsIkRTX01FUkNIQU5UX1VSTEtPIjoiaHR0cDovL3BheW1lbnQtZmFpbHVyZS97Y290aXphY2lvbklkfT9jSWQ9azNOTXp5OHciLCJEU19NRVJDSEFOVF9VUkxPSyI6Imh0dHA6Ly9wYXltZW50LXN1Y2Nlc3Mve2NvdGl6YWNpb25JZH0/Y0lkPWszTk16eTh3IiwiRFNfTUVSQ0hBTlRfTUVSQ0hBTlRDT0RFIjoiMDM0ODMwODY3NyIsIkRTX01FUkNIQU5UX1RFUk1JTkFMIjoiMDAxIiwiRFNfTUVSQ0hBTlRfQ1VSUkVOQ1kiOiI5NzgiLCJEU19NRVJDSEFOVF9UUkFOU0FDVElPTlRZUEUiOiIwIiwiRFNfTUVSQ0hBTlRfT1JERVIiOiIxNjA0NjkiLCJEU19NRVJDSEFOVF9BTU9VTlQiOiIxNjM5In0=",
// "urlPost":"https://sis-t.redsys.es:25443/sis/realizarPago/"},"coberturas":[{"importes":{"primaNeta":10.59,"tasasTotales":1.03,"primaTotal":11.62,"detallesTasas":[{"tasa":1,"importe":0.17},{"tasa":3,"importe":0.85},{"tasa":2,"importe":0.02}]},"codigo":"AU01","descripcion":null},{"importes":{"primaNeta":0.96,"tasasTotales":0.08,"primaTotal":1.04,"detallesTasas":[{"tasa":1,"importe":0.0},{"tasa":3,"importe":0.08},{"tasa":2,"importe":0.0}]},"codigo":"AU02","descripcion":null},{"importes":{"primaNeta":0.85,"tasasTotales":0.07,"primaTotal":0.92,"detallesTasas":[{"tasa":1,"importe":0.0},{"tasa":3,"importe":0.07},{"tasa":2,"importe":0.0}]},"codigo":"AU03","descripcion":null},{"importes":{"primaNeta":2.6,"tasasTotales":0.21,"primaTotal":2.81,"detallesTasas":[{"tasa":1,"importe":0.0},{"tasa":3,"importe":0.21},{"tasa":2,"importe":0.0}]},"codigo":"AU14","descripcion":null}]}
